import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Archive, RotateCcw, Users, AlertTriangle } from "lucide-react";
import { PageHeader, Btn, Field, inputCls, Modal, Card } from "@/components/cc";

export const TYPE_LABEL = {
  person: "Person", political_party: "Political Party", government: "Government",
  ministry: "Ministry", government_agency: "Government Agency", parliament: "Parliament / Legislature",
  public_institution: "Public Institution", private_company: "Private Company", ngo: "NGO / CSO",
  international_org: "International Organization", media_org: "Media Organization",
  security_institution: "Security / Law Enforcement", court: "Court / Judicial",
  educational_institution: "Educational Institution", traditional_authority: "Traditional Authority",
  religious_org: "Religious Organization", other_org: "Other Organization",
  sports_club: "Sports Club / Franchise", national_team: "National Team",
  league_competition: "League / Competition", sports_federation: "Sports Federation / Governing Body",
  venue: "Stadium / Venue",
};
export const STATUS_COLOR = {
  active: "text-emerald-400", inactive: "text-zinc-400", historical: "text-amber-400", unknown: "text-zinc-500",
};

function Stat({ label, value, color, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <div className={`font-tactical font-black text-2xl ${color || "text-white"}`}>{value ?? 0}</div>
      <div className="label-caps text-zinc-500 mt-1">{label}</div>
    </Card>
  );
}

export default function EntityProfiles() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canManage = (user?.roles || []).some((r) => ["editor", "super_admin"].includes(r));
  const [meta, setMeta] = useState(null);
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", entity_type: "", country: "", region: "", status: "", party: "", institution: "", tag: "" });
  const [tab, setTab] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [dupes, setDupes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [action, setAction] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const dupTimer = useRef(null);

  function blank() {
    return { canonical_name: "", display_name: "", entity_type: "", short_description: "", country: "", region: "", headquarters: "", website: "", status: "active", party: "", institution: "", tags: "", aliases: "", analyst_notes: "", full_name: "", common_name: "", honorific: "", gender: "", date_of_birth: "", nationality: "", occupation: "" };
  }

  const loadMeta = () => {
    api.get("/entities/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/entities/dashboard").then((r) => setDash(r.data)).catch(() => {});
  };
  const loadRows = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (tab === "archived") params.archived = true;
    setLoading(true); setRows([]);
    api.get("/entities/entities", { params }).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filters, tab]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { loadRows(); }, [loadRows]);

  const onNameChange = (v) => {
    setForm((f) => ({ ...f, canonical_name: v }));
    if (dupTimer.current) clearTimeout(dupTimer.current);
    if (v.trim().length < 2) { setDupes([]); return; }
    dupTimer.current = setTimeout(() => {
      api.get("/entities/duplicates", { params: { name: v.trim() } }).then((r) => setDupes(r.data)).catch(() => setDupes([]));
    }, 350);
  };

  const create = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form };
      payload.tags = payload.tags ? payload.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
      payload.aliases = payload.aliases ? payload.aliases.split(",").map((t) => t.trim()).filter(Boolean) : [];
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || (Array.isArray(payload[k]) && payload[k].length === 0)) delete payload[k]; });
      const { data } = await api.post("/entities/entities", payload);
      toast.success("Entity created");
      setOpen(false); setForm(blank()); setDupes([]); loadMeta();
      nav(`/command-center/entities/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runAction = async () => {
    if (busy || !action) return;
    setBusy(true);
    try {
      if (action.mode === "archive") await api.delete(`/entities/entities/${action.ent.id}`);
      else await api.post(`/entities/entities/${action.ent.id}/restore`);
      toast.success(action.mode === "archive" ? "Entity archived" : "Entity restored");
      setAction(null); loadRows(); loadMeta();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const isPerson = form.entity_type === "person";

  return (
    <div className="p-6">
      <PageHeader title="Entity Profiles" subtitle="Canonical profiles for people, organizations & institutions — one intelligence workspace per entity.">
        <Btn data-testid="ent-new-btn" disabled={!meta} onClick={() => { setForm(blank()); setDupes([]); setOpen(true); }}><Plus className="h-4 w-4" /> New Entity</Btn>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <Stat label="Total Active" value={dash?.total} testid="ent-stat-total" />
        <Stat label="People" value={dash?.people} color="text-sky-400" testid="ent-stat-people" />
        <Stat label="Organizations" value={dash?.organizations} color="text-violet-400" testid="ent-stat-orgs" />
        <Stat label="Political Parties" value={dash?.political_parties} color="text-emerald-400" testid="ent-stat-parties" />
        <Stat label="Govt. Institutions" value={dash?.government_institutions} color="text-amber-400" testid="ent-stat-gov" />
      </div>

      <Card className="p-4 mb-6">
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Field label="Search (name / alias)"><input data-testid="ent-filter-q" className={inputCls} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Name, alias, description" /></Field>
          <Field label="Type"><select data-testid="ent-filter-type" className={inputCls} value={filters.entity_type} onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}><option value="">All</option>{(meta?.entity_types || []).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}</select></Field>
          <Field label="Status"><select data-testid="ent-filter-status" className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}><option value="">All</option>{(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Country"><input data-testid="ent-filter-country" className={inputCls} value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} /></Field>
          <Field label="Region"><select data-testid="ent-filter-region" className={inputCls} value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}><option value="">All</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
          <Field label="Party"><input data-testid="ent-filter-party" className={inputCls} value={filters.party} onChange={(e) => setFilters({ ...filters, party: e.target.value })} /></Field>
          <Field label="Institution"><input data-testid="ent-filter-institution" className={inputCls} value={filters.institution} onChange={(e) => setFilters({ ...filters, institution: e.target.value })} /></Field>
          <Field label="Tag"><input data-testid="ent-filter-tag" className={inputCls} value={filters.tag} onChange={(e) => setFilters({ ...filters, tag: e.target.value })} /></Field>
        </div>
      </Card>

      <div className="flex items-center gap-4 border-b border-zinc-800 mb-4">
        {["active", "archived"].map((t) => (
          <button key={t} data-testid={`ent-tab-${t}`} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm font-semibold border-b-2 transition-colors capitalize ${tab === t ? "border-emerald-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>{t}</button>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full min-w-[840px] text-sm" data-testid="ent-table">
          <thead>
            <tr className="text-left label-caps text-zinc-500 border-b border-zinc-800">
              <th className="px-4 py-2.5">Entity</th><th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Aliases</th><th className="px-4 py-2.5">Country / Region</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">{tab === "archived" ? "Archived" : "Updated"}</th>
              {canManage && <th className="px-4 py-2.5">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {loading && <tr><td colSpan={canManage ? 7 : 6} className="px-4 py-6 text-zinc-600" data-testid="ent-loading">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={canManage ? 7 : 6} className="px-4 py-6 text-zinc-600" data-testid="ent-empty">{tab === "archived" ? "No archived entities." : "No entities match."}</td></tr>}
            {!loading && rows.map((e) => (
              <tr key={e.id} className={`hover:bg-zinc-900/50 ${tab === "active" ? "cursor-pointer" : ""}`} data-testid={`ent-row-${e.id}`} onClick={() => tab === "active" && nav(`/command-center/entities/${e.id}`)}>
                <td className="px-4 py-2.5 max-w-sm"><div className="font-medium truncate">{e.display_name}</div>{e.party && <span className="text-[0.65rem] text-emerald-400">{e.party}</span>}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{TYPE_LABEL[e.entity_type] || e.entity_type}</td>
                <td className="px-4 py-2.5 text-zinc-500 text-xs">{e.aliases?.length ? e.aliases.join(", ") : "—"}</td>
                <td className="px-4 py-2.5 text-zinc-400 text-xs">{[e.country, e.region].filter(Boolean).join(" / ") || "—"}</td>
                <td className={`px-4 py-2.5 font-mono-data uppercase text-xs ${STATUS_COLOR[e.status] || "text-zinc-400"}`}>{e.status}</td>
                <td className="px-4 py-2.5 text-zinc-600 text-xs">{tab === "archived" ? (e.deleted_at ? new Date(e.deleted_at).toLocaleDateString() : "—") : (e.updated_at ? new Date(e.updated_at).toLocaleDateString() : "—")}</td>
                {canManage && (
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {tab === "active" ? (
                      <button data-testid={`ent-archive-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setAction({ mode: "archive", ent: e }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-red-500/40 bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 transition-colors"><Archive className="h-3.5 w-3.5" /> Archive</button>
                    ) : (
                      <button data-testid={`ent-restore-${e.id}`} onClick={(ev) => { ev.stopPropagation(); setAction({ mode: "restore", ent: e }); }}
                        className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20 transition-colors"><RotateCcw className="h-3.5 w-3.5" /> Restore</button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={!!action} onClose={() => { if (!busy) setAction(null); }} title={action?.mode === "archive" ? "Archive entity" : "Restore entity"}>
        {action && (
          <div className="space-y-4" data-testid="ent-action-confirm">
            <p className="text-sm text-zinc-400">{action.mode === "archive" ? "Archive this entity? It is hidden from the active list but preserved with all aliases, roles, relationships, cross-system links, sources, media and audit — and can be restored anytime. Linked claims, promises, timelines and records are NOT affected." : "Restore this entity? All aliases, roles, relationships, links and history are preserved."}</p>
            <div className="text-sm font-mono-data break-words" data-testid="ent-action-name">{action.ent.display_name}</div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="ent-action-cancel" disabled={busy} onClick={() => setAction(null)}>Cancel</Btn>
              <Btn data-testid="ent-action-confirm-btn" disabled={busy} onClick={runAction} className="flex-1 justify-center">{busy ? "Working…" : action.mode === "archive" ? "Archive" : "Restore"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="New Entity" wide>
        <form onSubmit={create} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Canonical name"><input data-testid="ent-canonical" className={inputCls} required value={form.canonical_name} onChange={(e) => onNameChange(e.target.value)} /></Field>
            <Field label="Entity type"><select data-testid="ent-type" className={inputCls} required value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })}><option value="">Select…</option>{(meta?.entity_types || []).map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}</select></Field>
          </div>
          {dupes.length > 0 && (
            <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3" data-testid="ent-dupe-warning">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold mb-2"><AlertTriangle className="h-4 w-4" /> Possible existing matches — review before creating (not merged automatically)</div>
              <ul className="space-y-1 text-xs">
                {dupes.map((d) => <li key={d.id} className="text-zinc-300">• {d.display_name} <span className="text-zinc-500">({TYPE_LABEL[d.entity_type] || d.entity_type}{d.aliases?.length ? ` · ${d.aliases.join(", ")}` : ""})</span></li>)}
              </ul>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Display name (optional)"><input data-testid="ent-display" className={inputCls} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></Field>
            <Field label="Status"><select data-testid="ent-status" className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          </div>
          <Field label="Short description"><textarea data-testid="ent-short" className={inputCls} rows={2} value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Country"><input data-testid="ent-country" className={inputCls} value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
            <Field label="Region"><select data-testid="ent-region" className={inputCls} value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}><option value="">—</option>{(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}</select></Field>
            <Field label="HQ / Location"><input data-testid="ent-hq" className={inputCls} value={form.headquarters} onChange={(e) => setForm({ ...form, headquarters: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Party (if relevant)"><input data-testid="ent-party" className={inputCls} value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} /></Field>
            <Field label="Institution (if relevant)"><input data-testid="ent-institution" className={inputCls} value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} /></Field>
          </div>
          {isPerson && (
            <div className="rounded-sm border border-zinc-800 p-3 space-y-3" data-testid="ent-person-fields">
              <div className="label-caps text-zinc-500">Person details (optional)</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Full name"><input data-testid="ent-fullname" className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></Field>
                <Field label="Common / public name"><input data-testid="ent-commonname" className={inputCls} value={form.common_name} onChange={(e) => setForm({ ...form, common_name: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Honorific / title"><input data-testid="ent-honorific" className={inputCls} value={form.honorific} onChange={(e) => setForm({ ...form, honorific: e.target.value })} /></Field>
                <Field label="Gender"><select data-testid="ent-gender" className={inputCls} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}><option value="">—</option>{(meta?.genders || []).map((g) => <option key={g} value={g}>{g}</option>)}</select></Field>
                <Field label="Date of birth"><input type="date" data-testid="ent-dob" className={inputCls} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nationality"><input data-testid="ent-nationality" className={inputCls} value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} /></Field>
                <Field label="Occupation / profession"><input data-testid="ent-occupation" className={inputCls} value={form.occupation} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></Field>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Aliases (comma-separated)"><input data-testid="ent-aliases" className={inputCls} value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="NDC, National Democratic Congress" /></Field>
            <Field label="Tags (comma-separated)"><input data-testid="ent-tags" className={inputCls} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></Field>
          </div>
          <Field label="Website"><input data-testid="ent-website" className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
          <Field label="Analyst notes"><textarea data-testid="ent-notes" className={inputCls} rows={2} value={form.analyst_notes} onChange={(e) => setForm({ ...form, analyst_notes: e.target.value })} /></Field>
          <Btn data-testid="ent-save-btn" type="submit" disabled={saving} className="w-full justify-center"><Users className="h-4 w-4" /> {saving ? "Creating…" : "Create Entity"}</Btn>
        </form>
      </Modal>
    </div>
  );
}
